import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useSearchParams } from "react-router-dom";
import "react-day-picker/dist/style.css";
import AICallsTable from "../AICallsTable";
import { toast, ToastContainer } from "react-toastify";
import ApiEndPoints from "../../../../ServicesCalls/ApiEndPoints";
import { formatDate2 } from "../../../reusable-components/PageFormats";
// import SampleData from "../../../../Assets/mockData/tableSampleData.json";
import PageTableHeaderCols from "../../../reusable-components/PageTableHeaderCols";
import PageTableDateFilters from "../../../reusable-components/PageTableDateFilters";
import { getApiTokenFromLocalstorage } from "../../../reusable-components/PageFormats";

const POLLING_INTERVAL_MS = 60000;
export default function CompletedCalls({
  SelComplAppoint,
  SetSelComplAppoint,
  SetComplCallsStats,
}) {
  const calendarRef = useRef(null);
  const [searchParams] = useSearchParams();
  const urlFilter = searchParams.get("datefilter") || "Currentday";

  const [activeFilter, setActiveFilter] = useState(urlFilter);

  // Persist selected filter in localStorage and restore on reload if no datefilter param
  useEffect(() => {
    const savedFilter = localStorage.getItem("completedActiveFilter");
    if (savedFilter && !searchParams.get("datefilter")) {
      setActiveFilter(savedFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("completedActiveFilter", activeFilter);
  }, [activeFilter]);

  const [showCalendar, setShowCalendar] = useState(false);
  const [complCallsTableErr, setComplCallsTableErr] = useState(null);
  const [complCallsTableData, setComplCallsTableData] = useState(null);
  const [isCompletedCallsLoading, setIsComplCallsLoading] = useState(true);
  const [range, setRange] = useState(() => {
    const savedRange = localStorage.getItem("completedDateRange");
    if (savedRange) {
      try {
        const { from, to } = JSON.parse(savedRange);
        return {
          from: from ? new Date(from) : undefined,
          to: to ? new Date(to) : undefined,
        };
      } catch (e) {
        return { from: undefined, to: undefined };
      }
    }
    return { from: undefined, to: undefined };
  });

  useEffect(() => {
    localStorage.setItem(
      "completedDateRange",
      JSON.stringify({
        from: range.from ? range.from.toISOString() : null,
        to: range.to ? range.to.toISOString() : null,
      })
    );
  }, [range]);

  const TableHeaderCols = useMemo(() => {
    const headers = PageTableHeaderCols({ PageName: "CompletedCalls" });
    if (activeFilter === "Currentday") {
      return headers.filter((col) => {
        const key = Object.keys(col)[0];
        return col[key] !== "Appt Day";
      });
    }
    return headers;
  }, [activeFilter]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    }

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  const fetchComplCallsData = useCallback(
    async (typeOfReqBody) => {
      try {
        setComplCallsTableErr(null);
        const requestHeaders = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getApiTokenFromLocalstorage()}`,
          },
          body: JSON.stringify(typeOfReqBody),
        };
        const response = await fetch(
          ApiEndPoints.postAdminCompletedCalls(),
          requestHeaders
        );
        const data = await response.json();
        const dataSuccess = data?.message;
        const dataError = data?.error;

        if (!response.ok)
          throw new Error(
            dataSuccess || dataError || "Failed to Fetch AgentLogs API"
          );

        const dataCalls = data?.CompletedAgents;
        const dataStats = data?.CountSummary;

        const arrDataRes =
          data && dataCalls
            ? Array.isArray(dataCalls)
              ? dataCalls
              : [dataCalls]
            : null;
        const modifiedData =
          arrDataRes &&
          arrDataRes.map((item) => ({
            ...item,
            PatientStatus:
              item?.PatientStatus === "Pending"
                ? "No Response"
                : item?.PatientStatus,
          }));
        SetComplCallsStats(dataStats);
        setComplCallsTableData(modifiedData);
      } catch (err) {
        const errorMsg = err.message;
        console.error(errorMsg);
        setComplCallsTableErr(errorMsg);
        setComplCallsTableData(null);
        return;
      } finally {
        setIsComplCallsLoading(false);
      }
    },
    [SetComplCallsStats]
  );

  const handleFetchDataByActiveFilter = useCallback(
    (activeDateFilter, showSpinner = true) => {
      let apiRequestBody = {};
      if (showSpinner) setIsComplCallsLoading(true);
      switch (activeDateFilter) {
        case "Currentday":
          apiRequestBody = {
            AppointmentDate: formatDate2("", "current"),
            Day: "Currentday",
          };
          break;
        case "Custom":
          if (!range.from || !range.to) return;
          const toStr = formatDate2(range.to, "inputDate");
          const fromStr = formatDate2(range.from, "inputDate");
          apiRequestBody = { FromDate: fromStr, ToDate: toStr };
          setShowCalendar(false);
          break;
        default:
          break;
      }
      fetchComplCallsData(apiRequestBody);
    },
    [fetchComplCallsData, range]
  );

  useEffect(() => {
    const pollAppointments = () => {
      handleFetchDataByActiveFilter(activeFilter, false);
    };
    const intervalId = setInterval(pollAppointments, POLLING_INTERVAL_MS);
    handleFetchDataByActiveFilter(activeFilter); // First call with spinner
    return () => clearInterval(intervalId);
  }, [activeFilter]); //Don't add any dependencies even warning showing here.

  const handleCustomDateSubmit = () => {
    if (range.from && range.to && range.to >= range.from) {
      setShowCalendar(false);
      setActiveFilter("Custom");
      handleFetchDataByActiveFilter("Custom");
      toast.success("Date range applied successfully", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <>
      <ToastContainer />
      {isCompletedCallsLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 pr-2 mr-3 border-t-2 border-blue-800 rounded-full animate-spin"></div>
          <span className="py-8 text-center text-blue-500">
            Loading Completed Calls Data...
          </span>
        </div>
      ) : complCallsTableErr ? (
        <div className="py-8 text-center text-red-600">
          {complCallsTableErr}
        </div>
      ) : (
        complCallsTableData != null && (
          <div className="mt-4">
            {!SelComplAppoint && (
              <PageTableDateFilters
                range={range}
                setRange={setRange}
                PageName="CompletedCalls"
                calendarRef={calendarRef}
                activeFilter={activeFilter}
                showCalendar={showCalendar}
                setActiveFilter={setActiveFilter}
                setShowCalendar={setShowCalendar}
                handleCustomDateSubmit={handleCustomDateSubmit}
              />
            )}
            <AICallsTable
              PageName="CompletedCalls"
              TableColumns={TableHeaderCols}
              TableData={complCallsTableData}
              selAppoint={SelComplAppoint}
              onSelAppoint={SetSelComplAppoint}
            />
          </div>
        )
      )}
    </>
  );
}
